import type { ThreadPlacement } from "../../../api/threads/placementsApi";

/**
 * Where a Thread shows in the person's list (F44), as in T3 Code: active; woke, an active row
 * with a "Woke" badge until it is opened or dismissed; snoozed or settled, each in its own
 * section below the list. Pure, so the rules are tested in one place.
 */
export type InboxPlace = "active" | "woke" | "snoozed" | "settled";

/**
 * A snoozed Thread wakes when its time has come; the Thread wakes it earlier by asking a
 * person for input, which module C records as `woke`, so answering it doesn't undo the badge.
 */
export function inboxPlace(placement: ThreadPlacement | undefined, now: number): InboxPlace {
  switch (placement?.state) {
    case undefined:
    case "active":
      return "active";
    case "settled":
      return "settled";
    case "woke":
      return "woke";
    case "snoozed":
      return placement.snoozedUntil === null || Date.parse(placement.snoozedUntil) <= now ? "woke" : "snoozed";
  }
}

/** The next moment a snoozed Thread wakes by its time, after `now`; `null` if none does. */
export function nextWakeAt(placements: Iterable<ThreadPlacement>, now: number): number | null {
  let next: number | null = null;
  for (const placement of placements) {
    const until = placement.state === "snoozed" && placement.snoozedUntil !== null ? Date.parse(placement.snoozedUntil) : null;
    if (until !== null && until > now && (next === null || until < next)) {
      next = until;
    }
  }
  return next;
}

export type SnoozePresetKey = "hour" | "threeHours" | "evening" | "tomorrow" | "nextWeek";

export interface SnoozePreset {
  readonly key: SnoozePresetKey;
  readonly until: Date;
}

const HOUR = 60 * 60 * 1000;
const EVENING_HOUR = 18;
const MORNING_HOUR = 9;

function atHour(day: Date, hour: number, addDays = 0): Date {
  const moment = new Date(day);
  moment.setDate(moment.getDate() + addDays);
  moment.setHours(hour, 0, 0, 0);
  return moment;
}

/**
 * T3 Code's snooze choices, in local time: in one hour, in three hours, this evening at 18:00
 * while that is more than an hour away, tomorrow at 9:00, and next Monday at 9:00 unless that
 * is tomorrow.
 */
export function snoozePresets(now: Date): readonly SnoozePreset[] {
  const presets: SnoozePreset[] = [
    { key: "hour", until: new Date(now.getTime() + HOUR) },
    { key: "threeHours", until: new Date(now.getTime() + 3 * HOUR) },
  ];
  const evening = atHour(now, EVENING_HOUR);
  if (evening.getTime() - now.getTime() > HOUR) {
    presets.push({ key: "evening", until: evening });
  }
  const tomorrow = atHour(now, MORNING_HOUR, 1);
  presets.push({ key: "tomorrow", until: tomorrow });
  const nextMonday = atHour(now, MORNING_HOUR, (1 - now.getDay() + 7) % 7 || 7);
  if (nextMonday.getTime() !== tomorrow.getTime()) {
    presets.push({ key: "nextWeek", until: nextMonday });
  }
  return presets;
}
